import { useCallback } from "react";
import type {
  FormAction,
  ValidFormField,
} from "src/components/Form/Form.types.ts";
import type { FieldValues } from "react-hook-form";
import type {
  ValidFormData,
  ValidRequestData,
} from "src/components/Form/hooks/useResourceForm/useResourceForm.tsx";
import type { GlobalSettings } from "src/hooks/use-global-settings.ts";
import { reduceObject } from "@exonn/utils";

export const useTransformValuesToRequestData = <
  FormValues extends FieldValues = FieldValues,
  FormData extends ValidFormData = ValidFormData,
  RequestData extends ValidRequestData = ValidRequestData,
>({
  transformValuesToRequestData,
  globalSettings,
  processedFields,
}: {
  processedFields: Record<keyof FormValues, ValidFormField<FormValues>>;
  globalSettings: GlobalSettings;
  transformValuesToRequestData?: (
    values: FormValues,
    data: FormData,
    type: FormAction,
    globalSettings: GlobalSettings,
  ) => RequestData | Promise<RequestData>;
}) => {
  return useCallback(
    (
      values: FormValues,
      data: FormData,
      type: FormAction,
    ): RequestData | Promise<RequestData> => {
      delete values["__cache"];
      const processedValues = values;

      if (transformValuesToRequestData) {
        return transformValuesToRequestData(
          processedValues,
          data,
          type,
          globalSettings,
        );
      }

      const specialTransformations = reduceObject(
        processedFields,
        (acc, field, name) => {
          if (field.valueSetter) {
            return {
              ...acc,
              [name]: field.valueSetter(values[name], values),
            };
          }
          if (field.type === "text" || field.type === "textArea") {
            const value = values[name];
            return {
              ...acc,
              [name]: value === "" ? "__empty_string__" : value,
            };
          }
          if (field.type === "number" && field.emptyWhenZero) {
            const value = values[name];
            return {
              ...acc,
              [name]: value ? +value : 0,
            };
          }
          return acc;
        },
        {} as Record<string, any>,
      );

      return {
        ...(processedValues as unknown as RequestData),
        ...specialTransformations,
      };
    },
    [transformValuesToRequestData, globalSettings, processedFields],
  );
};
